import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { CentroCustoService } from '../../../../shared/services/apis/financas/centro-custo.service';
import { CentroCustoRequest } from '../../../../shared/services/requests/financas/centro-custo.request';
import { HeaderComponent } from '../../../../shared/components/header/header.component';

@Component({
  selector: 'app-centro-custo-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    LoadingBoxComponent,
    FooterActionComponent,
    HeaderComponent,
  ],
  templateUrl: './centro-custo-cadastro.component.html',
  styleUrl: './centro-custo-cadastro.component.css',
})
export class CentroCustoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

  public form: FormGroup;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public isEdicao: boolean = false;
  public idCentroCusto: number = 0;

  constructor(
    private formBuilder: FormBuilder,
    private centroCustoService: CentroCustoService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.route.params.subscribe(params => {
      if (params['idCentroCusto']) {
        this.idCentroCusto = +params['idCentroCusto'];
        this.isEdicao = true;
        this.carregarCentroCusto();
      }
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      nome: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(500)]],
      codigo: ['', [Validators.maxLength(30)]],
      isAtivo: [true],
    });
  }

  private carregarCentroCusto(): void {
    this.isLoading = true;
    this.centroCustoService.getById(this.idCentroCusto).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao carregar centro de custo.',
            AlertType.error
          );
          this.voltar();
          return;
        }
        if (res.centroCusto) {
          this.form.patchValue({
            nome: res.centroCusto.nome,
            codigo: res.centroCusto.codigo || '',
            isAtivo: res.centroCusto.isAtivo,
          });
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao carregar o centro de custo.', AlertType.error);
        this.voltar();
      },
    });
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request: CentroCustoRequest = {
      nome: this.form.get('nome')?.value?.trim(),
      codigo: this.form.get('codigo')?.value?.trim() || '',
      isAtivo: this.form.get('isAtivo')?.value,
    };

    if (this.isEdicao) {
      this.centroCustoService.update(this.idCentroCusto, request).subscribe({
        next: res => this.tratarRetorno(res, 'Centro de custo atualizado!'),
        error: err => this.tratarErro(err),
      });
    } else {
      this.centroCustoService.create(request).subscribe({
        next: res => this.tratarRetorno(res, 'Centro de custo cadastrado com sucesso!'),
        error: err => this.tratarErro(err),
      });
    }
  }

  private tratarRetorno(res: any, successMsg: string): void {
    this.isSaving = false;
    if (res.isError) {
      this.alertService.show(
        'Atenção',
        res.errorDescription || 'Erro ao salvar centro de custo.',
        AlertType.warning
      );
    } else {
      this.alertService.show('Sucesso', successMsg, AlertType.success);
      this.voltar();
    }
  }

  private tratarErro(err: any): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/financas/centros-custo']);
  }
}
